import {
	DocumentNode,
	OperationVariables,
	QueryHookOptions,
	QueryResult,
	TypedDocumentNode,
} from "@apollo/client";
import { useQuery } from "@apollo/client/react/hooks/useQuery";
import { useEffect, useMemo, useState } from "react";
import revalidateQueryCache from "src/api/graphql/variables/revalidateQueryCache";
import { getCurTime_S } from "src/shared/helpers/timeHelpers";

interface RevalidatedQueryOptions<TData, TVariables extends OperationVariables = OperationVariables>
	extends Omit<QueryHookOptions<TData, TVariables>, "fetchPolicy" | "nextFetchPolicy"> {
	/**
	 * The time in seconds to wait before revalidating the query.
	 *
	 * @default 600
	 */
	revalidationTime_S?: number;
	/**
	 * A function that returns the cache key for the revalidation cache.
	 *
	 * @default (variables) => JSON.stringify(variables)
	 */
	getRevalidationCacheKey?: (variables: TVariables | undefined) => string;
	/**
	 * The delay in milliseconds before revalidating the query.
	 */
	delay_MS?: number;
}

/**
 * A hook that fetches the query data and revalidates it after a certain amount of time.
 * This is a network fetch optimization over `useQuery` with fetchPolicy="cache-and-network" where
 * instead of refetching the query every time the page is loaded, this hook only refetches every x seconds.
 *
 * @param query The query document.
 * @param options The options for the query.
 * @returns The query result.
 */
const useRevalidatedQuery = <
	TData = any,
	TVariables extends OperationVariables = OperationVariables,
>(
	query: DocumentNode | TypedDocumentNode<TData, TVariables>,
	{
		getRevalidationCacheKey,
		delay_MS,
		...options
	}: RevalidatedQueryOptions<TData, TVariables> = {},
): QueryResult<TData, TVariables> => {
	const {
		loading: queryLoading,
		refetch,
		data,
		...result
	} = useQuery(query, {
		...options,
		fetchPolicy: "cache-first",
	});

	const revalidationCacheKey = useMemo(() => {
		const hash = (query as any).__meta__.hash as string;
		const cacheKey =
			getRevalidationCacheKey?.(options?.variables) ?? JSON.stringify(options?.variables);
		return `${hash}-${cacheKey}`;
	}, [getRevalidationCacheKey, options?.variables, query]);

	const shouldRevalidate = useMemo(() => {
		const cur_S = getCurTime_S();
		const revalidationTime_S = options?.revalidationTime_S ?? 600;
		const existing = (revalidateQueryCache() ?? {})[revalidationCacheKey];
		if (!existing) {
			return true;
		}
		const diff = cur_S - existing.lastUpdatedAt;
		return diff >= revalidationTime_S;
	}, [options?.revalidationTime_S, revalidationCacheKey]);
	const [loading, setLoading] = useState(queryLoading || shouldRevalidate);

	useEffect(() => {
		if (shouldRevalidate) {
			revalidateQueryCache({
				...revalidateQueryCache(),
				[revalidationCacheKey]: {
					lastUpdatedAt: getCurTime_S(),
				},
			});

			if (delay_MS) {
				const timeout = setTimeout(() => {
					refetch().finally(() => {
						setLoading(false);
					});
				});
				return () => clearTimeout(timeout);
			} else {
				refetch().finally(() => {
					setLoading(false);
				});
				return;
			}
		}
		return;
	}, [delay_MS, refetch, revalidationCacheKey, shouldRevalidate]);

	return {
		...result,
		data,
		refetch,
		loading: loading || queryLoading,
	};
};
export default useRevalidatedQuery;
